import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, signal } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import { RefreshButtonComponent } from '../../../shared/components/refresh-button.component';
import { CommissionApprovalItem, CommissionApprovalService } from './commission-approval.service';

type CommissionApprovalRow = Omit<
  CommissionApprovalItem,
  | 'bookingAmount'
  | 'saleCommission'
  | 'specialCommission'
  | 'commissionRate'
  | 'recoveryDeduction'
  | 'netPaymentAmount'
  | 'commissionAmount'
> & {
  bookingAmount: number;
  saleCommission: number;
  specialCommission: number;
  commissionRate: number | null;
  recoveryDeduction: number;
  netPaymentAmount: number;
  commissionAmount: number;
  tour: (Omit<NonNullable<CommissionApprovalItem['tour']>, 'tourPrice'> & { tourPrice: number }) | null;
};

@Component({
  selector: 'app-commission-approval-page',
  imports: [DatePipe, DecimalPipe, LucideAngularModule, StatusBadge, RefreshButtonComponent, TableModule, InputTextModule],
  templateUrl: './commission-approval-page.component.html',
})
export class CommissionApprovalPageComponent implements OnInit {
  private readonly overlay = inject(GlobalOverlayService);
  private readonly service = inject(CommissionApprovalService);
  readonly rows = signal<CommissionApprovalRow[]>([]);
  readonly isLoadingCommissions = signal(false);
  readonly selected = signal<CommissionApprovalRow | null>(null);

  ngOnInit(): void {
    void this.loadCommissions();
  }
  async loadCommissions(): Promise<void> {
    if (this.isLoadingCommissions()) return;
    this.isLoadingCommissions.set(true);
    this.overlay.showLoading('กำลังโหลดรายการค่าคอมมิชชั่นรออนุมัติ...');
    try {
      const items = await this.service.getPending();
      this.rows.set(
        items.map((item) => ({
          ...item,
          bookingAmount: Number(item.bookingAmount),
          saleCommission: Number(item.saleCommission),
          specialCommission: Number(item.specialCommission),
          commissionRate: item.commissionRate === null ? null : Number(item.commissionRate),
          recoveryDeduction: Number(item.recoveryDeduction),
          netPaymentAmount: Number(item.netPaymentAmount),
          commissionAmount: Number(item.commissionAmount),
          tour: item.tour ? { ...item.tour, tourPrice: Number(item.tour.tourPrice) } : null,
        })),
      );
    } catch (error: unknown) {
      this.showError('โหลดค่าคอมมิชชั่นไม่สำเร็จ', error);
    } finally {
      this.isLoadingCommissions.set(false);
      this.overlay.hideLoading();
    }
  }
  open(row: CommissionApprovalRow): void {
    this.selected.set(row);
  }
  close(): void {
    this.selected.set(null);
  }
  openAttachment(url: string | null): void {
    if (url) window.open(this.service.resolveFileUrl(url), '_blank', 'noopener,noreferrer');
  }

  commissionPerPerson(amount: number, travelerCount: number): number {
    return amount / Math.max(travelerCount, 1);
  }

  async approve(row: CommissionApprovalRow): Promise<void> {
    const confirmed = await this.overlay.confirm({
      title: 'อนุมัติค่าคอมมิชชั่น',
      message: row.recoveryDeduction > 0
        ? `ยอดคอม ${row.commissionAmount.toLocaleString()} บาท หักยอดค้าง ${row.recoveryDeduction.toLocaleString()} บาท และรับสุทธิ ${row.netPaymentAmount.toLocaleString()} บาท`
        : `ยืนยันค่าคอมมิชชั่น ${row.commissionAmount.toLocaleString()} บาท ให้ ${row.saleName}`,
      confirmText: 'อนุมัติ',
    });
    if (!confirmed) return;
    this.overlay.showLoading('กำลังอนุมัติค่าคอมมิชชั่น...');
    try {
      await this.service.approve(row.id, row.sourceType);
      this.remove(row);
      this.overlay.success('อนุมัติสำเร็จ', `${row.code} ถูกส่งให้ฝ่ายบัญชีตรวจสอบแล้ว`);
    } catch (error: unknown) {
      this.showError('อนุมัติไม่สำเร็จ', error);
    } finally {
      this.overlay.hideLoading();
    }
  }
  async reject(row: CommissionApprovalRow): Promise<void> {
    const confirmed = await this.overlay.confirm({
      title: 'ไม่อนุมัติค่าคอมมิชชั่น',
      message: `ยืนยันไม่อนุมัติ ${row.code} หรือไม่`,
      confirmText: 'ไม่อนุมัติ',
    });
    if (!confirmed) return;
    this.overlay.showLoading('กำลังบันทึกผลการอนุมัติ...');
    try {
      await this.service.reject(row.id, row.sourceType);
      this.remove(row);
      this.overlay.success('บันทึกผลแล้ว', `${row.code} ไม่ผ่านการอนุมัติ`);
    } catch (error: unknown) {
      this.showError('บันทึกผลไม่สำเร็จ', error);
    } finally {
      this.overlay.hideLoading();
    }
  }
  private remove(row: CommissionApprovalRow): void {
    this.rows.update((rows) =>
      rows.filter((item) => item.id !== row.id || item.sourceType !== row.sourceType),
    );
    this.close();
  }
  private showError(title: string, error: unknown): void {
    const message =
      error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
        ? error.error.message
        : 'กรุณาลองใหม่อีกครั้ง';
    this.overlay.error(title, message);
  }
}
